/* eslint-disable react/no-unescaped-entities */
import { Link, useParams, Navigate } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import SeoHead from "@/components/seo/SeoHead";
import { breadcrumb, webPage } from "@/components/seo/schemas";

// ─── Single source of truth for company/contact details on legal pages ───
const COMPANY = {
  legal_name: "LeadIQ Pro",
  trade_name: "LeadIQ Pro",
  website: "https://leadiqpro.io",
  product: "LeadIQ Pro",
  email: "support@app.leadiqpro.io",
  privacy_email: "support@app.leadiqpro.io",
  billing_email: "support@app.leadiqpro.io",
  address: "Address on file with the company — available on request",
  jurisdiction: "the United States of America and India",
  governing_law: "the laws of the United States of America and India, as applicable to the parties",
  effective_date: "February 1, 2026",
  last_updated: "February 1, 2026",
};

const PAGES = {
  terms: {
    title: "Terms & Conditions",
    intro:
      "These Terms govern your access to and use of the " +
      COMPANY.product +
      " platform. By creating an account, accessing the service, or completing a purchase, you agree to these Terms.",
    sections: [
      {
        h: "1. The service",
        b: [
          `${COMPANY.product} ("Service") is a software-as-a-service lead enrichment and sales intelligence platform operated by ${COMPANY.legal_name} ("we", "us", "our"). The Service ingests basic lead inputs (e.g., name, company, domain) and returns enriched data including company information, intent signals, contact discovery, and tailored scoring.`,
          `We reserve the right to modify, suspend, or discontinue any feature of the Service at any time. We will provide reasonable notice for material changes that adversely affect paying customers.`,
        ],
      },
      {
        h: "2. Eligibility & accounts",
        b: [
          "You must be at least 18 years old and legally able to enter contracts to use the Service. You agree to provide accurate, current, and complete information during registration.",
          "You are responsible for all activity that occurs under your account and for keeping your credentials confidential. Notify us immediately at " +
            COMPANY.email +
            " if you suspect unauthorized use.",
          "One account per person or entity. Sharing accounts across multiple users in violation of seat limits is prohibited and may result in suspension.",
        ],
      },
      {
        h: "3. Plans, trials & billing",
        b: [
          "We offer a 30-day free trial granting full feature access subject to a usage cap published on the pricing page. Trial accounts that do not upgrade revert to the Free plan when the trial ends.",
          "Paid plans renew monthly or annually at the price displayed at the time of purchase. You authorize us (or our payment processor) to charge your selected payment method on each renewal date until you cancel.",
          "Pricing is in U.S. Dollars unless otherwise stated. Indian customers with a valid GSTIN on file may receive invoices with applicable IGST. You are responsible for any taxes, duties, or levies imposed by your jurisdiction.",
          "Credit pack and seat add-on purchases are one-time charges. Plan upgrades take effect immediately; downgrades take effect at the end of the current billing cycle.",
        ],
      },
      {
        h: "4. Acceptable use",
        b: [
          "You agree not to: (a) use the Service for any unlawful, harmful, harassing, or fraudulent purpose; (b) reverse engineer, decompile, or attempt to extract the source code of the Service; (c) resell, sublicense, or redistribute enriched data in bulk without our prior written consent; (d) probe, scan, or test the Service's vulnerabilities, or breach security or authentication measures; (e) introduce malware, viruses, or harmful code; (f) use the Service in a manner that exceeds reasonable usage of computing resources or that interferes with other customers.",
          "Enriched data must be used in compliance with applicable laws, including but not limited to GDPR, CCPA/CPRA, India's DPDP Act, and the CAN-SPAM Act. You are solely responsible for ensuring your outreach has a lawful basis and respects do-not-contact requests.",
        ],
      },
      {
        h: "5. Data ownership & licensing",
        b: [
          "You retain ownership of any data you upload to the Service (e.g., lead CSVs, ICP profiles). You grant us a worldwide, non-exclusive, royalty-free license to process and use that data solely to operate, maintain, secure, and improve the Service.",
          "Enriched output that we produce is licensed to you for your internal business use during your active subscription. Resale, public distribution, or training of competing AI models on bulk enriched output is prohibited.",
          "We may produce aggregated, de-identified analytics from customer usage that do not identify any individual customer; we own such aggregated insights.",
        ],
      },
      {
        h: "6. Intellectual property",
        b: [
          `All rights, title, and interest in and to the Service, including all software, models, designs, content, trademarks (including "${COMPANY.product}", "Atom", "Spark", "Apex", and the LiQ mark), and documentation, are and remain the exclusive property of ${COMPANY.legal_name} and its licensors.`,
          "We grant you a limited, non-exclusive, non-transferable, revocable license to access and use the Service in accordance with these Terms.",
        ],
      },
      {
        h: "7. Third-party services",
        b: [
          "The Service integrates with third-party providers (payment processors, AI model providers, data-enrichment and email-validation providers, and others listed in our subprocessor exhibit). Your use of those features is also subject to the third party's terms and privacy policy.",
          "We are not responsible for outages, data accuracy, or pricing changes imposed by any third-party provider.",
        ],
      },
      {
        h: "8. Termination",
        b: [
          "You may cancel your subscription at any time from the Billing section of your account. Cancellation prevents future renewals; access continues through the end of the period you have paid for.",
          "We may suspend or terminate your account immediately if we determine, in our reasonable discretion, that you have violated these Terms, are creating a security or legal risk, or have failed to pay amounts due.",
          "Upon termination, you remain liable for any outstanding charges; we may retain your data as required by law or to defend against legal claims.",
        ],
      },
      {
        h: "9. Disclaimers",
        b: [
          'The Service is provided "as is" and "as available". To the maximum extent permitted by law, we disclaim all warranties, express or implied, including merchantability, fitness for a particular purpose, non-infringement, and accuracy of enriched data.',
          "Enrichment outputs depend on third-party web sources and may be incomplete, outdated, or incorrect. You are responsible for validating critical data before action.",
        ],
      },
      {
        h: "10. Limitation of liability",
        b: [
          "To the maximum extent permitted by law, our aggregate liability arising out of or relating to the Service shall not exceed the greater of (a) the amount you paid us in the 12 months preceding the event giving rise to the claim, or (b) USD 100.",
          "We are not liable for indirect, incidental, special, consequential, punitive, or exemplary damages, including lost profits, lost data, business interruption, or reputational harm, even if advised of the possibility of such damages.",
        ],
      },
      {
        h: "11. Indemnification",
        b: [
          "You agree to defend, indemnify, and hold harmless " +
            COMPANY.legal_name +
            " and its officers, employees, and agents from any claims, liabilities, damages, and expenses (including reasonable attorney's fees) arising from (a) your use of the Service, (b) your violation of these Terms, or (c) your violation of any third-party right, including any data-protection or anti-spam law.",
        ],
      },
      {
        h: "12. Governing law & disputes",
        b: [
          `These Terms are governed by ${COMPANY.governing_law}, without regard to conflict-of-law principles. Disputes shall be resolved in the competent courts located in the State of Delaware, USA, or in Bengaluru, India, whichever is closer to the customer's primary place of business, except that we may seek injunctive relief in any court of competent jurisdiction. The parties consent to personal jurisdiction in either forum.`,
        ],
      },
      {
        h: "13. Changes to these Terms",
        b: [
          "We may update these Terms from time to time. If we make material changes, we will notify you by email or via in-app notice at least 14 days before the changes take effect. Continued use of the Service after the effective date constitutes acceptance.",
        ],
      },
      {
        h: "14. Contact",
        b: [
          `Questions about these Terms? Email ${COMPANY.email}. For billing inquiries, ${COMPANY.billing_email}.`,
        ],
      },
    ],
  },

  privacy: {
    title: "Privacy Policy",
    intro:
      "This Privacy Policy explains how " +
      COMPANY.legal_name +
      ' ("we", "us") collects, uses, shares, and protects personal information when you use ' +
      COMPANY.product +
      ".",
    sections: [
      {
        h: "1. Information we collect",
        b: [
          "Account information: name, email, password (hashed), company, role.",
          "Billing information: legal name, billing address, country, tax ID (e.g., GSTIN, VAT, EIN), and partial payment details processed by Razorpay. We do not store full card numbers or CVV values.",
          "Usage information: the leads you upload, ICP profile details, search queries, enrichment events, IP address, browser and device metadata, and pages visited.",
          "Third-party data: enriched company and contact data obtained from public web sources and licensed third-party providers — see 'Sources of enriched data' below.",
          "Cookies and similar technologies: session tokens, anti-CSRF tokens, anonymous analytics. Strictly-necessary cookies cannot be disabled.",
        ],
      },
      {
        h: "2. How we use information",
        b: [
          "To provide, maintain, and secure the Service.",
          "To process payments, issue invoices, and prevent fraud.",
          "To deliver enriched output, fit scoring, and personalized features.",
          "To send transactional emails (invoice receipts, account alerts) and product updates you have opted into.",
          "To improve our AI models and product features using aggregated, de-identified usage data; we do not train on your raw uploaded leads or ICP details for other customers.",
          "To comply with legal obligations, enforce our terms, and protect our rights and the safety of users.",
        ],
      },
      {
        h: "3. Sources of enriched data",
        b: [
          "Enriched output is assembled from publicly accessible sources (company websites, news, job boards, LinkedIn public profiles, G2, etc.) and from licensed third-party providers. Where applicable, we rely on legitimate-interest or other lawful bases under GDPR/DPDP for processing third-party personal data presented to you in an enriched lead.",
          "If you believe an enriched record contains inaccurate information about you, please email " +
            COMPANY.privacy_email +
            " — we will review and correct or remove the record where legally required.",
        ],
      },
      {
        h: "4. Sharing & disclosure",
        b: [
          "Subprocessor categories: payment processors, AI model providers (LLM APIs), contact-discovery providers, email-validation providers, company-research providers, transactional email providers, opt-in outbound email (Gmail API when a user connects their own inbox), cloud database hosting, and application-platform hosting. Each subprocessor is contractually bound to confidentiality, security, and data-processing obligations.",
          "The current named subprocessor list is provided as an exhibit to our Data Processing Addendum (DPA) and is available on request to " +
            COMPANY.privacy_email +
            ". We will notify DPA-signed customers before adding or replacing a subprocessor.",
          "Legal: we may disclose information when required by law, court order, or to protect against fraud, security threats, or to enforce our terms.",
          "Business transfers: in the event of a merger, acquisition, or asset sale, your information may be transferred subject to confidentiality protections.",
          "We do not sell your personal information. See our 'Do Not Sell My Personal Information' notice for details applicable to California residents.",
        ],
      },
      {
        h: "5. Data retention",
        b: [
          "We retain your account and lead data for as long as your account is active and for up to 36 months after termination, except (a) invoices and billing records, which we retain for 7 years for tax and audit purposes, and (b) backup copies, which expire on standard rotation cycles up to 90 days.",
          "You can request earlier deletion under the rights section below.",
        ],
      },
      {
        h: "6. Your rights",
        b: [
          "Depending on where you live, you may have the right to: (a) access the personal data we hold about you; (b) correct inaccurate data; (c) delete your data (subject to legal retention requirements); (d) port your data; (e) object to or restrict processing; (f) withdraw consent; (g) lodge a complaint with your supervisory authority.",
          "To exercise any right, email " +
            COMPANY.privacy_email +
            ". We will respond within 30 days. We will not discriminate against you for exercising your rights.",
        ],
      },
      {
        h: "7. Security",
        b: [
          "We implement industry-standard administrative, technical, and physical safeguards including encryption in transit (TLS 1.2+), encryption at rest, secret rotation, least-privilege access, intrusion detection, and regular backups. No system is 100% secure; if a breach affects your data we will notify you and the appropriate regulator without undue delay.",
        ],
      },
      {
        h: "8. International transfers",
        b: [
          "We process data in India and the United States, and may transfer data to other jurisdictions where our subprocessors operate. Transfers from the EEA/UK rely on Standard Contractual Clauses or equivalent safeguards.",
        ],
      },
      {
        h: "9. GDPR (EU/UK) compliance",
        b: [
          `${COMPANY.product} is GDPR-ready. If you are located in the EEA, UK, or Switzerland, the ${COMPANY.legal_name} entity acts as data controller for your account data and as data processor for lead data you upload; you remain the controller of any personal data you supply.`,
          "Lawful basis: we process business-contact data under Article 6(1)(f) — legitimate interest in enabling B2B sales research on publicly available information. Account and billing data are processed under Article 6(1)(b) (contract) and Article 6(1)(c) (legal obligation).",
          "Data Processing Addendum (DPA): a signed DPA incorporating the EU Standard Contractual Clauses (Module Two) is available on request to " +
            COMPANY.privacy_email +
            ". Business, Scale, and Enterprise customers may execute it before or after purchase at no additional cost.",
          "EU / UK representative: available on request via " +
            COMPANY.privacy_email +
            " for individuals who wish to raise a matter through a local representative. You also have the right to lodge a complaint with your supervisory authority (in the UK, the ICO; in the EU, your national DPA).",
          "Data-subject requests: any individual — including a prospect appearing in an enriched lead — may email " +
            COMPANY.privacy_email +
            " to access, correct, delete, restrict or object to our processing of their data. We respond within 30 days at no charge.",
        ],
      },
      {
        h: "10. Children's privacy",
        b: [
          "The Service is not directed to anyone under 18. We do not knowingly collect data from children. If you believe we have, contact " +
            COMPANY.privacy_email +
            " and we will delete it.",
        ],
      },
      {
        h: "11. Changes to this policy",
        b: [
          "We may update this Privacy Policy from time to time. Material changes will be announced in-app or via email at least 14 days before they take effect. The 'Last updated' date below indicates the most recent revision.",
        ],
      },
      {
        h: "12. Contact",
        b: [
          `Email our privacy team at ${COMPANY.privacy_email}. Postal address available on request via ${COMPANY.email}.`,
        ],
      },
    ],
  },

  refunds: {
    title: "Refund Policy",
    intro:
      "This Refund Policy applies to all purchases made on " +
      COMPANY.product +
      ", including subscription plans, credit packs, and seat add-ons.",
    sections: [
      {
        h: "1. All sales are final — no refunds",
        b: [
          "All payments made to " +
            COMPANY.legal_name +
            " are final and non-refundable, including but not limited to: monthly and annual subscription charges, credit pack purchases, additional seat purchases, and any other one-time fees.",
          "We do not provide pro-rated, partial, or full refunds in the event of (a) cancellation before the end of a billing cycle, (b) account suspension or termination for breach of our Terms, (c) reduced usage during the billing period, (d) feature removal or change, (e) downgrade to a lower-priced plan, or (f) dissatisfaction with enrichment output quality.",
          "We strongly encourage all prospective customers to use our 30-day free trial — which grants full feature access — to evaluate the platform thoroughly before committing to a paid plan. Trial users are not charged.",
        ],
      },
      {
        h: "2. Cancellations",
        b: [
          "You may cancel your subscription at any time via the Billing section of your account. Cancellation stops future renewals only; you will continue to have access to the Service through the end of the current paid billing period. We do not refund the unused portion.",
        ],
      },
      {
        h: "3. Duplicate charges & billing errors",
        b: [
          "If you believe you have been billed in error (e.g., a duplicate charge from a payment-system anomaly, or a charge after a confirmed cancellation), please contact " +
            COMPANY.billing_email +
            " within 30 days of the charge with your invoice number and a clear description of the issue. We will investigate and, if we confirm a billing error on our part, correct it by issuing a credit or, at our discretion, a refund of the erroneous amount.",
          "Refunds for genuine billing errors are processed back to the original payment method via Razorpay and typically appear within 7-10 business days, subject to your bank's processing times.",
        ],
      },
      {
        h: "4. Chargebacks",
        b: [
          "Please contact us at " +
            COMPANY.billing_email +
            " before initiating a chargeback. Filing a chargeback without first contacting us may result in immediate account suspension and a permanent ban from future use of the Service. Chargebacks initiated for charges that comply with this policy will be contested with supporting evidence.",
        ],
      },
      {
        h: "5. Service unavailability",
        b: [
          "If the Service is materially unavailable for more than 72 consecutive hours due to a fault on our side, customers on paid plans may request service credit equal to the pro-rated value of the affected period. Service credits are non-monetary and applied to your next invoice; they are not refundable to your payment method.",
        ],
      },
      {
        h: "6. Statutory rights",
        b: [
          "Nothing in this policy limits any rights you may have under applicable consumer-protection law that cannot be excluded by contract. Where local law mandates a cooling-off or refund period, we will honor it strictly to the extent legally required.",
        ],
      },
      {
        h: "7. Contact",
        b: [
          `Billing questions and refund requests for confirmed billing errors: ${COMPANY.billing_email}. We aim to respond within 2 business days.`,
        ],
      },
    ],
  },

  "do-not-sell": {
    title: "Do Not Sell My Personal Information",
    intro:
      "Under the California Consumer Privacy Act (CCPA) and California Privacy Rights Act (CPRA), California residents have the right to direct businesses not to sell their personal information.",
    sections: [
      {
        h: "1. We do not sell your personal information",
        b: [
          COMPANY.legal_name +
            " does not sell personal information for monetary consideration as the term 'sale' is commonly understood. We do not share personal information with third parties for those parties' independent marketing purposes.",
          "We do use third-party subprocessors (payment processors, AI model providers, contact and data-enrichment providers) strictly to provide the Service to you. These providers are contractually bound to use your information only for the purposes for which we disclose it.",
        ],
      },
      {
        h: "2. Sharing for cross-context behavioural advertising",
        b: [
          "We do not engage in 'sharing' as defined by CPRA for cross-context behavioural advertising purposes. We do not place advertising cookies on the Service for third-party ad targeting.",
        ],
      },
      {
        h: "3. Your rights as a California resident",
        b: [
          "Right to know: request disclosure of personal information we have collected about you in the past 12 months.",
          "Right to delete: request deletion of personal information we hold about you, subject to lawful exceptions.",
          "Right to correct: request correction of inaccurate personal information.",
          "Right to opt out of sale / sharing: while we do not sell or share personal information as defined under the law, you may still submit a request — we will confirm in writing that no such activity is occurring.",
          "Right to limit use of sensitive personal information: we do not process sensitive personal information for purposes that would require this right; you may still submit a confirmation request.",
          "Right to non-discrimination: we will not deny you services, charge different prices, or provide a lesser quality of service if you exercise any privacy right.",
        ],
      },
      {
        h: "4. How to submit a request",
        b: [
          "Email " +
            COMPANY.privacy_email +
            ' with the subject line "Do Not Sell My Personal Information" or "CCPA Request" along with your account email and a description of your request. Verification: we may need to verify your identity (e.g., by confirming you can authenticate to the email associated with your account) before processing.',
          "Authorized agents may submit requests on your behalf with a signed authorization or power of attorney.",
          "We will acknowledge your request within 10 business days and respond substantively within 45 days, with a possible 45-day extension when reasonably necessary, as permitted by law.",
        ],
      },
      {
        h: "5. Categories of personal information collected (past 12 months)",
        b: [
          "Identifiers (name, email, IP address); commercial information (transaction history, plan); internet activity (pages visited, features used); professional information (role, company); inferences drawn from the above (e.g., fit score, opportunity score); geolocation (general, derived from IP).",
        ],
      },
      {
        h: "6. Categories of sources",
        b: [
          "Directly from you (registration, profile, leads uploaded); automatically via our Service (usage, logs, cookies); from third-party enrichment providers and public sources (company information, news, job boards, public LinkedIn profiles, G2 reviews).",
        ],
      },
      {
        h: "7. Contact",
        b: [
          "Privacy questions, including CCPA/CPRA requests: " +
            COMPANY.privacy_email +
            ".",
        ],
      },
    ],
  },
};

export default function LegalPage() {
  const { slug } = useParams();
  const page = PAGES[slug];
  if (!page) return <Navigate to="/" replace />;

  return (
    <main className="min-h-screen bg-[#fafaf7]" data-testid={`legal-page-${slug}`}>
      <SeoHead
        title={`${page.title} — LeadIQ Pro`}
        description={page.intro}
        path={`/legal/${slug}`}
        type="article"
        jsonLd={[
          breadcrumb([
            { name: "Home", url: "/" },
            { name: "Legal", url: "/legal/privacy" },
            { name: page.title, url: `/legal/${slug}` },
          ]),
          webPage({
            name: `${page.title} — LeadIQ Pro`,
            description: page.intro,
            path: `/legal/${slug}`,
          }),
        ]}
      />
      <header className="border-b border-ink-100 bg-white/95">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-5 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3 group">
            <img src="/logo-128.png" alt="LeadIQ Pro" width="32" height="32" className="w-8 h-8" />
            <span className="font-display font-extrabold text-ink-900 text-sm">LeadIQ Pro</span>
          </Link>
          <Link
            to="/"
            className="text-[10px] uppercase tracking-widestx font-semibold text-ink-500 hover:text-ink-900 inline-flex items-center gap-1"
          >
            <ArrowLeft className="w-3 h-3" />
            Home
          </Link>
        </div>
      </header>

      <article className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-12 sm:py-16">
        <div className="text-[10px] uppercase tracking-widestx font-mono text-ink-400 mb-3">
          {COMPANY.product} · Legal
        </div>
        <h1 className="font-display font-black text-4xl sm:text-5xl text-ink-900 leading-tight mb-4">
          {page.title}
        </h1>
        <p className="text-base text-ink-700 leading-relaxed mb-2">{page.intro}</p>
        <p className="text-xs text-ink-400 mb-12">
          Effective {COMPANY.effective_date} · Last updated {COMPANY.last_updated}
        </p>

        <div className="space-y-10">
          {page.sections.map((s, i) => (
            <section key={i}>
              <h2 className="font-display font-extrabold text-xl text-ink-900 mb-3 leading-snug">
                {s.h}
              </h2>
              <div className="space-y-3">
                {s.b.map((para, j) => (
                  <p key={j} className="text-sm sm:text-base text-ink-700 leading-relaxed">
                    {para}
                  </p>
                ))}
              </div>
            </section>
          ))}
        </div>

        <div className="mt-16 pt-8 border-t border-ink-100">
          <div className="flex flex-wrap gap-x-6 gap-y-2 text-[10px] uppercase tracking-widestx font-semibold text-ink-500">
            <Link to="/legal/terms" className="hover:text-ink-900">Terms & Conditions</Link>
            <Link to="/legal/privacy" className="hover:text-ink-900">Privacy Policy</Link>
            <Link to="/legal/refunds" className="hover:text-ink-900">Refund Policy</Link>
            <Link to="/legal/do-not-sell" className="hover:text-ink-900">Do Not Sell My Info</Link>
          </div>
          <p className="text-xs text-ink-400 mt-4">
            © {new Date().getFullYear()} {COMPANY.legal_name}. All rights reserved.
          </p>
        </div>
      </article>
    </main>
  );
}
